import React, { useEffect } from "react";

/**
 * An in-page confirm dialog. Native window.confirm is swallowed by embedded browsers (owner, 2026-10-13: the Re-Render
 * button "doesnt seem to work" in the app's built-in browser — the confirm read as Cancel and nothing was sent), so
 * actions that need a yes ask here instead. Escape or the backdrop cancels.
 */
const BTN = "!h-9 w-full sm:w-auto justify-center whitespace-nowrap px-4";

export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  secondary,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** An optional second action beside the confirm (e.g. "Upgrade to premium"). */
  secondary?: { label: string; onClick: () => void } | null;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onCancel} aria-hidden data-testid="confirm-backdrop" />
      <div
        className="fixed left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100%-2rem)] max-w-md card p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="confirm-modal"
      >
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        {/* Narrow screens: full-width buttons stacked, the main action on top. Wider: one right-aligned row.
            Labels never wrap ("Re-" / "render" inside a fixed-height button). */}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end" data-testid="confirm-actions">
          <button type="button" className={`btn-secondary ${BTN}`} onClick={onCancel} data-testid="confirm-cancel">
            Cancel
          </button>
          {secondary && (
            <button type="button" className={`btn-secondary ${BTN}`} onClick={secondary.onClick} data-testid="confirm-secondary">
              {secondary.label}
            </button>
          )}
          <button type="button" className={`btn-primary ${BTN}`} onClick={onConfirm} autoFocus data-testid="confirm-ok">
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}
